import { useState } from 'react'
import { useRecordDecision } from '../../api/queries/dealMutations'
import { userFacingMessage } from '../../api/errors'
import { DECISION_ACTION_LABEL, RECOMMENDATION_LABEL } from '../pipeline/labels'
import type { DecisionValue } from '../../api/types'
import styles from './DecisionButtons.module.css'

/**
 * Keep investing / Pause / Stop investing — the product's central act.
 *
 * It lives in `deals` rather than `pipeline` because the decision is the deal's
 * data (docs/16 section 6.2); the board card imports it. It is domain-shaped, so
 * it is not promoted to `ui/` — being used twice is not what makes a component
 * generic (section 6.3).
 *
 * **Nothing is predicted.** Recording a decision resolves a pending
 * re-evaluation and can move the card to another column, both decided by the
 * server. So the control shows a pending state and renders whatever the response
 * says (section 8.5). The old app disabled the buttons and re-fetched too; what
 * is new is that no local copy of the verdict exists to go stale.
 */
const ORDER: readonly DecisionValue[] = ['keep_investing', 'pause', 'stop_investing']

export function DecisionButtons({
  dealId,
  current,
  variant,
  promptWhenUndecided = false,
}: {
  dealId: string
  current: DecisionValue | null
  variant: 'card' | 'panel'
  promptWhenUndecided?: boolean
}) {
  const record = useRecordDecision(dealId)
  const [pendingValue, setPendingValue] = useState<DecisionValue | null>(null)

  function choose(value: DecisionValue) {
    setPendingValue(value)
    record.mutate(value, { onSettled: () => setPendingValue(null) })
  }

  return (
    <>
      <div className={`${styles.group} ${styles[variant]}`} role="group" aria-label="Record your decision">
        {ORDER.map((value) => (
          <button
            key={value}
            type="button"
            className={`${styles.button} ${styles[value]} ${current === value ? styles.active : ''}`}
            aria-pressed={current === value}
            disabled={record.isPending}
            aria-busy={pendingValue === value}
            onClick={() => choose(value)}
          >
            {variant === 'card' ? DECISION_ACTION_LABEL[value] : RECOMMENDATION_LABEL[value]}
          </button>
        ))}
      </div>

      {promptWhenUndecided && !current && !record.isPending ? (
        <p className={styles.prompt}>Decision needed</p>
      ) : null}

      {record.isError ? (
        <p className={styles.failed} role="alert">
          Your decision was not saved. {userFacingMessage(record.error)}
        </p>
      ) : null}
    </>
  )
}
